import React, { useState, useCallback, useMemo } from "react";
import type { CustomThemeFormProps, ThemeField, ThemeCard } from "./types";
import {
  getFieldType,
  calculateNumberStep,
  updateNestedValue,
  normalizeColorValue,
  generateFieldKey,
  isValidColorForPreview,
} from "./themeUtils";

/**
 * CustomThemeForm - Dynamic form for editing custom theme colors
 * Appears only when custom theme is selected
 * Uses card-based layout with expandable sections for color groups
 */
export const CustomThemeForm: React.FC<CustomThemeFormProps> = ({
  customTheme,
  onThemeChange,
}) => {
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());

  // Toggle card expansion
  const toggleCard = useCallback((cardTitle: string) => {
    setExpandedCards((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(cardTitle)) {
        newSet.delete(cardTitle);
      } else {
        newSet.add(cardTitle);
      }
      return newSet;
    });
  }, []);

  // Handle field value changes
  const handleFieldChange = useCallback(
    (path: string[], value: any) => {
      const normalizedValue =
        getFieldType(value) === "color" ? normalizeColorValue(value) : value;

      const updatedTheme = updateNestedValue(
        customTheme,
        path,
        normalizedValue
      );
      onThemeChange(updatedTheme);
    },
    [customTheme, onThemeChange]
  );

  // Generate theme cards from the theme object
  const themeCards = useMemo((): ThemeCard[] => {
    const cards: ThemeCard[] = [];

    // Basic Properties (inline)
    const basicFields: ThemeField[] = [
      {
        path: ["mode"],
        label: "mode",
        value: customTheme.mode,
        type: getFieldType(customTheme.mode),
      },
      {
        path: ["contrastThreshold"],
        label: "contrastThreshold",
        value: customTheme.contrastThreshold,
        type: getFieldType(customTheme.contrastThreshold),
      },
      {
        path: ["tonalOffset"],
        label: "tonalOffset",
        value: customTheme.tonalOffset,
        type: getFieldType(customTheme.tonalOffset),
      },
    ];

    cards.push({
      title: "Basic Properties",
      fields: basicFields,
      isExpanded: expandedCards.has("Basic Properties"),
    });

    // Color Groups
    const colorGroups = [
      { key: "primary", title: "Primary Colors" },
      { key: "secondary", title: "Secondary Colors" },
      { key: "error", title: "Error Colors" },
      { key: "warning", title: "Warning Colors" },
      { key: "info", title: "Info Colors" },
      { key: "success", title: "Success Colors" },
    ];

    colorGroups.forEach(({ key, title }) => {
      const colorGroup = customTheme[key as keyof typeof customTheme] as any;
      if (colorGroup && typeof colorGroup === "object") {
        const fields: ThemeField[] = Object.entries(colorGroup).map(
          ([subKey, value]) => ({
            path: [key, subKey],
            label: `${key}.${subKey}`,
            value,
            type: getFieldType(value),
          })
        );

        cards.push({
          title,
          fields,
          isExpanded: expandedCards.has(title),
        });
      }
    });

    // Text Colors
    if (customTheme.text) {
      const textFields: ThemeField[] = Object.entries(customTheme.text).map(
        ([key, value]) => ({
          path: ["text", key],
          label: `text.${key}`,
          value,
          type: getFieldType(value),
        })
      );

      cards.push({
        title: "Text Colors",
        fields: textFields,
        isExpanded: expandedCards.has("Text Colors"),
      });
    }

    // Background Colors
    if (customTheme.background) {
      const backgroundFields: ThemeField[] = Object.entries(
        customTheme.background
      ).map(([key, value]) => ({
        path: ["background", key],
        label: `background.${key}`,
        value,
        type: getFieldType(value),
      }));

      cards.push({
        title: "Background Colors",
        fields: backgroundFields,
        isExpanded: expandedCards.has("Background Colors"),
      });
    }

    // Grey Palette
    if (customTheme.grey) {
      const greyFields: ThemeField[] = Object.entries(customTheme.grey).map(
        ([key, value]) => ({
          path: ["grey", key],
          label: `grey.${key}`,
          value,
          type: getFieldType(value),
        })
      );

      cards.push({
        title: "Grey Palette",
        fields: greyFields,
        isExpanded: expandedCards.has("Grey Palette"),
      });
    }

    // Action Colors
    if (customTheme.action) {
      const actionFields: ThemeField[] = Object.entries(customTheme.action).map(
        ([key, value]) => ({
          path: ["action", key],
          label: `action.${key}`,
          value,
          type: getFieldType(value),
        })
      );

      cards.push({
        title: "Action Colors",
        fields: actionFields,
        isExpanded: expandedCards.has("Action Colors"),
      });
    }

    // Divider
    cards.push({
      title: "Other Properties",
      fields: [
        {
          path: ["divider"],
          label: "divider",
          value: customTheme.divider,
          type: getFieldType(customTheme.divider),
        },
      ],
      isExpanded: expandedCards.has("Other Properties"),
    });

    return cards;
  }, [customTheme, expandedCards]);

  // Render individual field
  const renderField = useCallback(
    (field: ThemeField) => {
      const { path, label, value, type } = field;
      const fieldKey = generateFieldKey(path);

      const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let newValue: any = e.target.value;

        if (type === "number") {
          newValue = parseFloat(newValue) || 0;
        }

        handleFieldChange(path, newValue);
      };

      return (
        <div key={fieldKey} className="theme-field">
          <label className="theme-field-label" htmlFor={fieldKey}>
            {label}
          </label>
          <div className="theme-field-input">
            {type === "color" && (
              <div
                className="color-preview-square"
                style={{
                  backgroundColor: isValidColorForPreview(value)
                    ? value
                    : "#ccc",
                }}
                title={`Preview: ${value}`}
              />
            )}
            {label === "mode" ? (
              <select
                id={fieldKey}
                value={value}
                onChange={(e) => handleFieldChange(path, e.target.value)}
                className="theme-field-control"
              >
                <option value="light">Light</option>
                <option value="dark">Dark</option>
              </select>
            ) : (
              <input
                id={fieldKey}
                type={type === "number" ? "number" : "text"}
                value={value}
                onChange={handleInputChange}
                step={
                  type === "number" ? calculateNumberStep(value) : undefined
                }
                className="theme-field-control"
                placeholder={
                  type === "color" ? "#000000 или rgba(0,0,0,0.5)" : ""
                }
              />
            )}
          </div>
        </div>
      );
    },
    [handleFieldChange]
  );

  // Render theme card
  const renderCard = useCallback(
    (card: ThemeCard) => {
      const isExpanded = expandedCards.has(card.title);

      return (
        <div key={card.title} className="theme-card">
          <div
            className="theme-card-header"
            onClick={() => toggleCard(card.title)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                toggleCard(card.title);
              }
            }}
          >
            <h4 className="theme-card-title">{card.title}</h4>
            <span className="theme-card-toggle">{isExpanded ? "▼" : "▶"}</span>
          </div>
          {isExpanded && (
            <div className="theme-card-content">
              {card.fields.map(renderField)}
            </div>
          )}
        </div>
      );
    },
    [expandedCards, toggleCard, renderField]
  );

  return (
    <div className="custom-theme-form">
      <div className="custom-theme-form-header">
        <h3>Custom Theme Configuration</h3>
        <p>
          Edit the colors and properties of your custom theme. Changes will be
          reflected in the generated code.
        </p>
      </div>
      <div className="custom-theme-form-content">
        {themeCards.map(renderCard)}
      </div>
    </div>
  );
};
